import React from 'react';
import { RefreshCcw } from 'lucide-react';
import MultiSelectDropdownInline from '../multi-select-inline';
import { FilterState } from './types';

interface JobsiteProgressFiltersProps {
  activeFilters: FilterState;
  setActiveFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  sectors: { id: string; name: string }[];
  provinces: { id: string; name: string }[];
  statusOptions: { id: string; name: string }[];
  handleApplyFilters: () => void;
  handleFilterClearAll: () => void;
  isJobsiteProgressFiltersValid: () => boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  localUnions?: { id: string; name: string }[];
}

const JobsiteProgressFilters: React.FC<JobsiteProgressFiltersProps> = ({
  activeFilters,
  setActiveFilters,
  sectors,
  provinces,
  statusOptions,
  handleApplyFilters,
  handleFilterClearAll,
  isJobsiteProgressFiltersValid,
  setLoading,
  localUnions = []
}) => {
  return (
    <div className="bg-white rounded-[12px] shadow p-3 sm:p-4 mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 items-end">
        {/* Start Date */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Start Date</label>
          <input
            id="inline-start-date-input"
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:h-10 md:w-[95%]"
            value={activeFilters.startDate ? activeFilters.startDate.split('T')[0] : ''}
            max={activeFilters.endDate ? activeFilters.endDate.split('T')[0] : undefined}
            onChange={(e) => {
              setActiveFilters(prev => ({
                ...prev,
                startDate: e.target.value ? new Date(e.target.value).toISOString() : ''
              }));
            }}
          />
        </div>

        {/* End Date */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">End Date</label>
          <input
            id="inline-end-date-input"
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:h-10 md:w-[95%]"
            value={activeFilters.endDate ? activeFilters.endDate.split('T')[0] : ''}
            min={activeFilters.startDate ? activeFilters.startDate.split('T')[0] : undefined}
            onChange={(e) => {
              setActiveFilters(prev => ({
                ...prev,
                endDate: e.target.value ? new Date(e.target.value).toISOString() : ''
              }));
            }}
          />
        </div>

        {/* Sector Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Sector</label>
          <div id="inline-sector-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="sector-dropdown"
              options={sectors}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  sector: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.sector}
              placeholder="Select sectors"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Province Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Province</label>
          <div id="inline-province-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="province-dropdown"
              options={provinces}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  province: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.province}
              placeholder="Select provinces"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Status Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Jobsite Status</label>
          <div id="inline-status-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="status-dropdown"
              options={statusOptions}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  status: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.status}
              placeholder="Select statuses"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Local Unions Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Local Union</label>
          <div id="local-unions-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="local-unions-dropdown"
              options={localUnions}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  localUnion: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.localUnion || []}
              placeholder="Select local unions"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Apply Button - Full width on small screens, auto width on larger screens */}
        <div className="w-full sm:col-span-2 lg:col-span-3 xl:col-span-5 flex justify-end mt-4 gap-2">
          <button
            id="apply-inline-filters-button"
            className={`btn-primary whitespace-nowrap flex-1 sm:flex-none ${!isJobsiteProgressFiltersValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleApplyFilters}
            disabled={!isJobsiteProgressFiltersValid()}
          >
            Apply Filters
          </button>
          <button
            id="refresh-button"
            className="btn-secondary whitespace-nowrap"
            onClick={() => {
              setLoading(true);
              handleFilterClearAll();
            }}
          >
            <RefreshCcw className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobsiteProgressFilters;
